import { AlertTriangle, Check, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { JobSummaryResult } from '@/modules/hiring-manager/chat/chatResults';
import {
  completenessLabelText,
  nextStepCopy,
  verdictShortText,
} from '@/modules/jobs/signal/derivations';
import {
  isStrongVerdict,
  toCompletenessLabel,
  toNextStep,
  toVerdict,
} from '@/modules/jobs/signal/verdict';
import { Button } from '@/modules/ui/components/button/Button';

// The reply each card button sends: the word the summary asks for, so a click is the same as typing it.
const cardActions: Record<
  string,
  { label: string; pendingLabel: string; reply: string }
> = {
  create: { label: 'Create job', pendingLabel: 'Creating…', reply: 'create' },
  publish: {
    label: 'Publish job',
    pendingLabel: 'Publishing…',
    reply: 'publish',
  },
  save: { label: 'Save changes', pendingLabel: 'Saving…', reply: 'save' },
  edit_manually: {
    label: 'Edit manually',
    pendingLabel: 'Opening the editor…',
    reply: 'Edit manually',
  },
};

const modeLabel: Record<JobSummaryResult['mode'], string> = {
  create: 'New job',
  draft: 'Draft',
  edit: 'Changes to a live job',
};

// Shown in the heading, the skills check and the description block instead of the details list.
const ownLabels = new Set(['Job title', 'Description']);

interface JobCardProps {
  summary: JobSummaryResult;
  /** Sends the button's reply as the user's next message. */
  onAction: (reply: string) => void;
  disabled: boolean;
  /** The message being sent: the button whose reply it is shows it's working, where the HM clicked. */
  sending?: string;
}

/** The job the chat is shaping, as its latest summary shows it, with what Finlay checked and what to do next. */
export function JobCard({
  summary,
  onAction,
  disabled,
  sending,
}: JobCardProps) {
  const title = summary.fields.find((field) => field.label === 'Job title');
  const description = summary.fields.find(
    (field) => field.label === 'Description',
  );
  const details = summary.fields.filter((field) => !ownLabels.has(field.label));
  const nextStep = summary.signal && toNextStep(summary.signal.nextStep);
  const actions = summary.actions.flatMap((action) => {
    const button = cardActions[action];
    return button ? [button] : [];
  });

  return (
    <article
      aria-labelledby="chat-job-card-title"
      className="flex flex-col gap-6"
    >
      <header className="flex flex-col gap-1">
        <span className="text-xs font-medium text-neutral-500">
          {modeLabel[summary.mode]}
        </span>
        <h2
          className="text-[17px] leading-6 font-semibold tracking-[-0.01em]"
          id="chat-job-card-title"
        >
          {title?.value ?? 'Untitled job'}
        </h2>
      </header>

      {summary.signal && (
        <section className="flex flex-col gap-1 text-[13px]">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">How searchable is this job?</h3>
            <span className="text-muted-foreground text-xs capitalize">
              {completenessLabelText(toCompletenessLabel(summary.signal.label))}
            </span>
          </div>
          {nextStep && (
            <p className="text-muted-foreground">
              Next:{' '}
              <span className="text-foreground font-medium">
                {nextStepCopy(nextStep)}
              </span>
            </p>
          )}
        </section>
      )}

      {summary.skills.length > 0 && (
        <section className="flex flex-col gap-2 text-[13px]">
          <h3 className="font-semibold">Skills check</h3>
          <ul className="flex flex-col gap-1.5">
            {summary.skills.map((skill) => {
              const verdict = toVerdict(skill.verdict ?? '');
              return (
                <li className="flex items-center gap-2" key={skill.display}>
                  {isStrongVerdict(verdict) ? (
                    <Check className="text-semantic-560 size-3.5 shrink-0" />
                  ) : (
                    <AlertTriangle className="text-semantic-540 size-3.5 shrink-0" />
                  )}
                  <span className="truncate">{skill.display}</span>
                  <span className="text-muted-foreground ml-auto shrink-0 text-xs">
                    {verdictShortText(verdict)}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {details.length > 0 && (
        <dl className="flex flex-col gap-2.5 text-[13px]">
          {details.map((field) => (
            <div
              className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] gap-3"
              key={field.label}
            >
              <dt className="text-neutral-500">{field.label}</dt>
              <dd className="break-words whitespace-pre-line">{field.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {description && (
        <section className="flex flex-col gap-1.5 text-[13px]">
          <h3 className="font-semibold">Description</h3>
          <p className="line-clamp-6 break-words whitespace-pre-line">
            {description.value}
          </p>
        </section>
      )}

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((action, index) => {
            const pending = disabled && sending === action.reply;
            return (
              <Button
                className={cn(pending && 'disabled:opacity-100')}
                disabled={disabled}
                key={action.reply}
                onClick={() => onAction(action.reply)}
                size="sm"
                variant={index === 0 ? 'primary' : 'outline'}
              >
                {pending && <Loader2 className="size-3.5 animate-spin" />}
                {pending ? action.pendingLabel : action.label}
              </Button>
            );
          })}
        </div>
      )}
    </article>
  );
}
